import { faEdit, faTrash } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import { withTranslation } from 'react-i18next';

import { getDayDate } from '../../../common/date';
import { getMonthsTranslations } from '../../../common/stats';
import { useLanguage } from '../../../contexts/LanguageContext';
import Action from '../../Action';
import CalendarWebsiteLogo from '../CalendarWebsiteLogo';
import Card from '../Card';
import CardActions from '../CardActions';
import DateDisplay from '../DateDisplay';
import Heading from '../Heading';

import * as style from './style.module.css';

const getField = (contents, field, language) => {
    // User language
    for (const content of contents) {
        if (content.language === language && content[field]) {
            return content[field];
        }
    }

    // English (default)
    for (const content of contents) {
        if (content.language === 'en' && content[field]) {
            return content[field];
        }
    }

    // Fallback, any language
    for (const content of contents) {
        if (content[field]) {
            return content[field];
        }
    }

    return null;
}

const colors = {
    CONTEST: 'var(--theme-blue)',
    IRL: 'var(--theme-purple)',
    SPONSOR: 'var(--theme-green)',
};

const CalendarEvent = ({ id, slug, startDate, endDate, type, contest, website = {}, contents = [], t, insideScrollable = false, allowedActions = [], onDelete = null }) => {
    const today = getDayDate();

    const { currentInterfaceLanguage } = useLanguage();

    const title = (getField(contents, 'title', currentInterfaceLanguage) || '').replaceAll('/', ' / ').replaceAll('/  ', '/ ').replaceAll('  /', ' /');
    const description = getField(contents, 'description', currentInterfaceLanguage);
    const announcementLink = getField(contents, 'announcementLink', currentInterfaceLanguage);
    const feedbackLink = getField(contents, 'feedbackLink', currentInterfaceLanguage);
    const contestLink = contest && contest.isResultsPublic ? `/contests/${contest.id}/${contest.slug}` : null;

    const action = contestLink || feedbackLink || announcementLink || null;

    const color = colors[type] || colors.CONTEST;

    const date = parseInt(startDate.split('-')[2], 10);
    const month = parseInt(startDate.split('-')[1], 10);
    const year = parseInt(startDate.split('-')[0], 10);

    const comparableDate = (endDate !== '0000-00-00' ? endDate : null) || startDate || today;

    const monthsTranslations = getMonthsTranslations(t);

    const actions = [
        ...(allowedActions.includes('EDIT') ? [{ title: t('Edit'), icon: faEdit, action: `/calendar/${id}/${slug}/edit` }] : []),
        ...(allowedActions.includes('DELETE') ? [{ title: t('Delete'), icon: faTrash, action: onDelete ? () => onDelete(id) : null }] : []),
    ];

    return <Card id={id} className={style.card} disablePadding>
        <Action action={action} className={`${style.event} ${insideScrollable ? style.insideScrollable : ''}`}>
            <div className={`${style.date} ${comparableDate < today ? style.ended : ''}`} style={{ background: color }}>
                {date > 0 && <div className={style.dateDate}>{date}</div>}
                {monthsTranslations?.[month] && <div className={style.dateMonth}>{monthsTranslations?.[month]?.substring(0, 3)}</div>}
                {year > 0 && <div className={style.dateYear}>{year}</div>}
            </div>
            <div className={style.content}>
                <div className={style.head}>
                    <Heading h={5} className={style.title}>{title}</Heading>
                    {website?.code && <Action action={website.link} className={style.logoLink}>
                        <CalendarWebsiteLogo className={style.logo} id={website?.code} alt={website?.title} />
                    </Action>}
                </div>
                <div className={style.description}>
                    {endDate && type === 'CONTEST' && <div className={style.deadline}>
                        {t('Deadline:')} <DateDisplay timestamp={endDate} showHours={false} />
                    </div>}
                    {description}
                </div>
            </div>
        </Action>
        <CardActions actions={actions} />
    </Card>
}

export default React.memo(withTranslation()(CalendarEvent));